import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { PageDataService } from '../core/page-data.service';
import { SeoService } from '../core/seo.service';
import { SiteConfigService } from '../core/site-config.service';
import { SupportCategory, SupportFaq, SupportFormField, SupportMethod, SupportService } from '../widgets/support/support.service';

@Component({
  selector: 'app-support-page',
  imports: [FormsModule, RouterLink],
  template: `
    <section class="support-hero">
      <nav class="breadcrumbs" aria-label="Breadcrumb">
        <a [routerLink]="i18n.routePath('home')">{{ i18n.t('home') }}</a>
        <span>/</span>
        @if (currentCategory()) {
          <a [routerLink]="i18n.routePath('support')">{{ i18n.t('support') }}</a>
          <span>/</span>
          <strong>{{ currentCategory()?.name }}</strong>
        } @else {
          <strong>{{ i18n.t('support') }}</strong>
        }
      </nav>
      <p class="eyebrow">{{ copy().center }}</p>
      <h1>{{ currentCategory()?.name || copy().title }}</h1>
      <p class="lead">
        @if (currentCategory()) {
          {{ copy().categoryLead }}
        } @else {
          {{ copy().lead }}
        }
      </p>
      <nav class="hero-actions">
        <a [routerLink]="i18n.routePath('supportTickets')">{{ copy().myTickets }}</a>
        @if (currentCategory()) {
          <a [routerLink]="i18n.routePath('support')">{{ copy().supportHome }}</a>
        }
      </nav>
    </section>

    @if (isBdsmbeds()) {
      <section class="presale-help" aria-labelledby="presale-help-title">
        <header>
          <p class="eyebrow">BEFORE YOU ORDER</p>
          <h2 id="presale-help-title">Get the product details you need to decide</h2>
          <p>Tell us which product you are considering and what you need to confirm. We will keep the request tied to that product.</p>
          @if (supportContext(); as context) {
            <div class="support-context" role="status"><strong>Current product:</strong> {{ context.product }} <span>·</span> {{ context.topic }}</div>
          }
        </header>
        <div class="presale-topic-grid">
          @for (topic of bdsmbedsTopics; track topic.key) {
            <a [href]="'#support-options'" (click)="selectSupportTopic(topic.key)">
              <span aria-hidden="true">{{ topic.icon }}</span>
              <strong>{{ topic.title }}</strong>
              <small>{{ topic.description }}</small>
            </a>
          }
        </div>
      </section>
    }

    <section class="support-grid" id="support-options">
      @if (visibleCategories().length) {
        <div class="category-grid">
          @for (category of visibleCategories(); track category.id) {
            <a class="category-card" [routerLink]="categoryLink(category)">
              <span>{{ category.hasChildren ? copy().subcategories : copy().detail }}</span>
              <h2>{{ category.name }}</h2>
              <p>{{ category.hasChildren ? copy().chooseSubcategory : copy().viewHelp }}</p>
            </a>
          }
        </div>
      } @else {
        <div class="support-content">
          <section class="card">
            <p class="eyebrow">{{ copy().faqAndHelp }}</p>
            <h2>{{ currentCategory()?.name || copy().detail }}</h2>
            @if (helpText()) {
              <div class="help-copy">{{ helpText() }}</div>
            }
            @if (faqs().length) {
              @for (faq of faqs(); track faq.id) {
                <details>
                  <summary>{{ faq.title }}</summary>
                  <p>{{ faq.content }}</p>
                </details>
              }
            } @else {
              @if (!isBdsmbeds()) {
                <p class="empty">{{ copy().noFaq }}</p>
              } @else {
                <p class="empty">Choose the relevant topic above and include the product name, selected option and the detail you want confirmed.</p>
              }
            }
          </section>

          <section class="method-grid">
            @for (method of methods(); track method.id) {
              @if (method.id > 0) {
                <article>
                  <span>{{ method.type }}</span>
                  <h3>{{ method.title }}</h3>
                  <p>{{ method.description }}</p>
                  <button type="button" (click)="beginTicket(method)" [disabled]="ticketBusy()">{{ copy().createWithMethod }}</button>
                </article>
              }
            }
            @if (!hasUsableMethods() && !isBdsmbeds()) {
              <article>
                <span>ticket</span>
                <h3>{{ copy().createTicket }}</h3>
                <p>{{ copy().generalTicketLead }}</p>
                <button type="button" (click)="beginTicket(defaultTicketMethod())" [disabled]="ticketBusy()">{{ copy().createGeneralTicket }}</button>
              </article>
            }
          </section>

          @if (selectedMethod(); as selected) {
            <section class="ticket-form card">
              <p class="eyebrow">{{ selected.title }}</p>
              <h2>{{ copy().createTicket }}</h2>
              <p class="form-note">{{ copy().formLead }}</p>
              <form (ngSubmit)="submitTicket()">
                @for (field of ticketFields(); track field.id) {
                  <label>
                    <span>{{ field.title }}@if (field.required) { <b>*</b> }</span>
                    @if (field.type === 'textarea' || field.type === 'longtext') {
                      <textarea [name]="field.formKey" [(ngModel)]="ticketValues[field.formKey]" [required]="field.required" [attr.minlength]="field.minlength || null" [attr.maxlength]="field.maxlength || null" rows="5"></textarea>
                    } @else {
                      <input [name]="field.formKey" [type]="field.type === 'email' ? 'email' : 'text'" [(ngModel)]="ticketValues[field.formKey]" [required]="field.required" [attr.minlength]="field.minlength || null" [attr.maxlength]="field.maxlength || null" />
                    }
                    @if (field.minlength || field.maxlength) {
                      <small class="field-constraint">
                        {{ fieldLength(field) }} {{ copy().characters }}
                        @if (field.minlength) { · {{ copy().minimum }} {{ field.minlength }} }
                        @if (field.maxlength) { · {{ copy().maximum }} {{ field.maxlength }} }
                      </small>
                    }
                  </label>
                }
                @if (ticketError()) { <p class="ticket-error">{{ ticketError() }}</p> }
                <div class="ticket-actions">
                  <button type="button" class="secondary" (click)="cancelTicket()">{{ copy().cancel }}</button>
                  <button type="submit" [disabled]="ticketBusy()">{{ ticketBusy() ? copy().sending : copy().send }}</button>
                </div>
              </form>
            </section>
          }
        </div>
      }
    </section>
  `,
  styles: [`
    .support-hero,
    .support-grid {
      margin: 0 auto;
      max-width: 1180px;
    }

    .support-hero {
      background: color-mix(in srgb, var(--surface) 84%, transparent);
      border: 1px solid var(--border);
      border-radius: 28px;
      box-shadow: 0 18px 48px color-mix(in srgb, var(--shadow) 46%, transparent);
      padding: clamp(22px, 4vw, 36px);
    }

    .support-hero h1 {
      font-size: clamp(30px, 5vw, 48px);
      margin: 8px 0;
    }

    .breadcrumbs {
      align-items: center;
      color: var(--muted);
      display: flex;
      flex-wrap: wrap;
      font-size: 13px;
      font-weight: 900;
      gap: 8px;
      margin-bottom: 16px;
    }

    .breadcrumbs a,
    .breadcrumbs strong {
      color: var(--ink);
      text-decoration: none;
    }

    .hero-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 28px;
    }

    .hero-actions a,
    .category-card,
    .card,
    .method-grid article {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 30px;
      box-shadow: 0 18px 46px color-mix(in srgb, var(--shadow) 56%, transparent);
    }

    .hero-actions a {
      color: var(--ink);
      font-weight: 950;
      padding: 12px 16px;
      text-decoration: none;
    }

    .support-grid {
      margin-top: 22px;
    }

    .presale-help {
      background: color-mix(in srgb, var(--accent) 7%, var(--surface));
      border: 1px solid color-mix(in srgb, var(--accent) 28%, var(--border));
      border-radius: 30px;
      display: grid;
      gap: 22px;
      grid-template-columns: minmax(240px, .72fr) minmax(0, 1.28fr);
      margin: 22px auto 0;
      max-width: 1180px;
      padding: clamp(22px, 4vw, 34px);
    }

    .presale-help h2 { color: var(--ink); font-size: clamp(25px, 3vw, 36px); margin: 6px 0 10px; }
    .presale-help header > p:last-of-type { color: var(--muted); line-height: 1.6; margin: 0; }
    .presale-topic-grid { display: grid; gap: 12px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .presale-topic-grid a { background: var(--card); border: 1px solid var(--border); border-radius: 20px; color: var(--ink); display: grid; gap: 6px; padding: 17px; text-decoration: none; }
    .presale-topic-grid a:hover,
    .presale-topic-grid a:focus-visible { border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 14%, transparent); }
    .presale-topic-grid a > span { color: var(--accent); font-size: 1.25rem; }
    .presale-topic-grid small { color: var(--muted); line-height: 1.45; }
    .support-context { background: var(--card); border: 1px solid var(--border); border-radius: 14px; color: var(--muted); margin-top: 16px; padding: 11px 13px; }
    .support-context strong { color: var(--ink); }

    .category-grid,
    .method-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .category-card {
      color: var(--ink);
      display: grid;
      gap: 12px;
      min-height: 180px;
      padding: 24px;
      text-decoration: none;
    }

    .category-card span,
    .method-grid span {
      color: var(--accent);
      font-size: 12px;
      font-weight: 950;
      letter-spacing: .12em;
      text-transform: uppercase;
    }

    .category-card h2,
    .card h2,
    .method-grid h3 {
      color: var(--ink);
      margin: 0;
    }

    .category-card p,
    .card p,
    .method-grid p,
    .empty {
      color: var(--muted);
      line-height: 1.55;
    }

    .card {
      padding: 24px;
    }

    details {
      border-top: 1px solid var(--border);
      padding: 16px 0;
    }

    summary {
      color: var(--ink);
      cursor: pointer;
      font-weight: 950;
    }

    .method-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-top: 20px;
    }

    .method-grid article {
      display: grid;
      gap: 12px;
      padding: 22px;
    }

    .method-grid button {
      background: var(--ink);
      border: 0;
      border-radius: 999px;
      color: var(--paper);
      cursor: pointer;
      font: inherit;
      font-weight: 950;
      padding: 12px 16px;
    }

    .method-grid button:disabled,
    .ticket-actions button:disabled { cursor: wait; opacity: .55; }

    .ticket-form { margin-top: 20px; }
    .ticket-form form { display: grid; gap: 14px; }
    .ticket-form label { display: grid; gap: 7px; }
    .ticket-form label span { color: var(--muted); font-size: .85rem; font-weight: 850; }
    .ticket-form label b { color: var(--accent); }
    .field-constraint { color: var(--muted); font-size: .78rem; }
    .ticket-form input,
    .ticket-form textarea { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; color: var(--ink); font: inherit; padding: 12px 14px; width: 100%; }
    .ticket-form textarea { resize: vertical; }
    .form-note { margin: 0; }
    .ticket-actions { display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end; margin-top: 6px; }
    .ticket-actions button { background: var(--ink); border: 0; border-radius: 999px; color: var(--paper); cursor: pointer; font: inherit; font-weight: 950; padding: 12px 18px; }
    .ticket-actions .secondary { background: transparent; border: 1px solid var(--border); color: var(--ink); }
    .ticket-error { color: #bd4260 !important; font-weight: 800; }

    @media (max-width: 900px) {
      .category-grid,
      .method-grid,
      .presale-help {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 560px) {
      .presale-topic-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class SupportPage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly i18n = inject(I18nService);
  private readonly pageData = inject(PageDataService);
  private readonly support = inject(SupportService);
  private readonly seo = inject(SeoService);
  private readonly site = inject(SiteConfigService);
  protected readonly currentCategory = signal<SupportCategory | null>(null);
  protected readonly visibleCategories = signal<SupportCategory[]>([]);
  protected readonly faqs = signal<SupportFaq[]>([]);
  protected readonly helpText = signal<string | null>(null);
  protected readonly methods = signal<SupportMethod[]>([]);
  protected readonly selectedMethod = signal<SupportMethod | null>(null);
  protected readonly ticketFields = signal<SupportFormField[]>([]);
  protected readonly ticketValues: Record<string, unknown> = {};
  protected readonly ticketBusy = signal(false);
  protected readonly ticketError = signal('');
  protected readonly bdsmbedsTopics = [
    { key: 'dimensions', icon: '↔', title: 'Fit and dimensions', description: 'Confirm mattress fit, outer frame measurements and room clearance.' },
    { key: 'configuration', icon: '◇', title: 'Configuration', description: 'Check finishes, options and compatible product combinations.' },
    { key: 'delivery', icon: '▣', title: 'Delivery and assembly', description: 'Ask about access, delivery planning and assembly requirements.' },
    { key: 'returns', icon: '↩', title: 'Returns', description: 'Review the return conditions that apply before you place an order.' }
  ];
  protected readonly copy = computed(() => ({
    tr: {
      center: 'Destek merkezi', title: 'Nasıl yardımcı olalım?', categoryLead: 'Alt kategori varsa bir sonraki adıma geç; yoksa SSS ve destek seçeneklerini incele.',
      lead: 'Önce destek konunu seç. Sayfayı yenilediğinde aynı noktadan devam edebilirsin.', myTickets: 'Destek taleplerim',
      supportHome: 'Destek ana sayfası', subcategories: 'Alt kategoriler', detail: 'Destek detayı',
      chooseSubcategory: 'Devam ederek daha net bir destek konusu seç.', viewHelp: 'SSS ve destek türlerini gör.',
      faqAndHelp: 'SSS ve yardım', noFaq: 'Bu kategori için henüz SSS yok. Destek talebi oluşturarak ekibe ulaşabilirsin.',
      createWithMethod: 'Bu türle talep oluştur', createTicket: 'Destek talebi oluştur',
      generalTicketLead: 'Uygun destek türü yoksa genel destek talebi ile ilerleyebilirsin.', createGeneralTicket: 'Genel talep oluştur', formLead: 'Mesajını ve istenen bilgileri doldurup destek ekibine gönder.', cancel: 'Vazgeç', sending: 'Gönderiliyor...', send: 'Talebi gönder', characters:'karakter', minimum:'en az', maximum:'en fazla', requiredField:'zorunlu alan', current:'şu an', invalidEmail:'geçerli bir e-posta adresi yazın'
    },
    en: {
      center: 'Support center', title: 'How can we help?', categoryLead: 'Continue to a subcategory or review the FAQs and support options.',
      lead: 'Choose a support topic. You can return to the same place after refreshing.', myTickets: 'My support requests',
      supportHome: 'Support home', subcategories: 'Subcategories', detail: 'Support details',
      chooseSubcategory: 'Continue to choose a more specific support topic.', viewHelp: 'View FAQs and support options.',
      faqAndHelp: 'FAQ and help', noFaq: 'No FAQs are linked to this category yet. Create a request to contact the team.',
      createWithMethod: 'Create request', createTicket: 'Create support request',
      generalTicketLead: 'Use a general support request when no specific method is available.', createGeneralTicket: 'Create general request', formLead: 'Complete the requested information and send it to support.', cancel: 'Cancel', sending: 'Sending...', send: 'Send request', characters:'characters', minimum:'minimum', maximum:'maximum', requiredField:'is required', current:'current', invalidEmail:'enter a valid email address'
    },
    de: {
      center: 'Supportcenter', title: 'Wie können wir helfen?', categoryLead: 'Wähle eine Unterkategorie oder sieh dir FAQ und Supportoptionen an.',
      lead: 'Wähle zuerst dein Thema. Nach dem Aktualisieren kehrst du an dieselbe Stelle zurück.', myTickets: 'Meine Supportanfragen',
      supportHome: 'Support-Startseite', subcategories: 'Unterkategorien', detail: 'Supportdetails',
      chooseSubcategory: 'Wähle ein genaueres Supportthema.', viewHelp: 'FAQ und Supportoptionen ansehen.',
      faqAndHelp: 'FAQ und Hilfe', noFaq: 'Für diese Kategorie gibt es noch keine FAQ. Erstelle eine Anfrage an unser Team.',
      createWithMethod: 'Anfrage erstellen', createTicket: 'Supportanfrage erstellen',
      generalTicketLead: 'Wenn keine passende Option verfügbar ist, nutze eine allgemeine Anfrage.', createGeneralTicket: 'Allgemeine Anfrage erstellen', formLead: 'Fülle die Angaben aus und sende sie an den Support.', cancel: 'Abbrechen', sending: 'Wird gesendet...', send: 'Anfrage senden', characters:'Zeichen', minimum:'mindestens', maximum:'höchstens', requiredField:'ist erforderlich', current:'aktuell', invalidEmail:'gültige E-Mail-Adresse eingeben'
    },
    es: {
      center: 'Centro de soporte', title: '¿Cómo podemos ayudarte?', categoryLead: 'Elige una subcategoría o consulta las preguntas y opciones de soporte.',
      lead: 'Elige primero un tema. Podrás volver al mismo lugar al actualizar.', myTickets: 'Mis solicitudes',
      supportHome: 'Inicio de soporte', subcategories: 'Subcategorías', detail: 'Detalles de soporte',
      chooseSubcategory: 'Elige un tema de soporte más específico.', viewHelp: 'Ver preguntas y opciones de soporte.',
      faqAndHelp: 'Preguntas y ayuda', noFaq: 'Aún no hay preguntas para esta categoría. Crea una solicitud para contactar al equipo.',
      createWithMethod: 'Crear solicitud', createTicket: 'Crear solicitud de soporte',
      generalTicketLead: 'Usa una solicitud general si no hay una opción específica.', createGeneralTicket: 'Crear solicitud general', formLead: 'Completa la información y envíala al equipo de soporte.', cancel: 'Cancelar', sending: 'Enviando...', send: 'Enviar solicitud', characters:'caracteres', minimum:'mínimo', maximum:'máximo', requiredField:'es obligatorio', current:'actual', invalidEmail:'introduce un correo válido'
    },
      fr: {
      center: 'Centre d’aide', title: 'Comment pouvons-nous vous aider ?', categoryLead: 'Choisissez une sous-catégorie ou consultez la FAQ et les options d’aide.',
      lead: 'Choisissez d’abord un sujet. Vous reviendrez au même endroit après actualisation.', myTickets: 'Mes demandes',
      supportHome: 'Accueil du support', subcategories: 'Sous-catégories', detail: 'Détails du support',
      chooseSubcategory: 'Choisissez un sujet d’aide plus précis.', viewHelp: 'Voir la FAQ et les options d’aide.',
      faqAndHelp: 'FAQ et aide', noFaq: 'Aucune FAQ pour cette catégorie. Créez une demande pour contacter l’équipe.',
      createWithMethod: 'Créer une demande', createTicket: 'Créer une demande d’aide',
      generalTicketLead: 'Utilisez une demande générale si aucune option précise n’est disponible.', createGeneralTicket: 'Créer une demande générale', formLead: 'Complétez les informations demandées et envoyez-les au support.', cancel: 'Annuler', sending: 'Envoi...', send: 'Envoyer la demande', characters:'caractères', minimum:'minimum', maximum:'maximum', requiredField:'est obligatoire', current:'actuel', invalidEmail:'saisissez une adresse e-mail valide'
    }
  })[this.i18n.lang()]);

  constructor() {
    this.route.paramMap.subscribe((params) => {
      void this.load(params.get('slug') ?? '');
    });
  }

  protected categoryLink(category: SupportCategory): string {
    return this.i18n.routePath('support', { slug: category.slug }).replace(/\/$/, '') + `/${category.slug}`;
  }

  protected isBdsmbeds(): boolean {
    return this.site.config().brandKey === 'bdsmbeds';
  }

  protected hasUsableMethods(): boolean {
    return this.methods().some((method) => method.id > 0);
  }

  protected supportContext(): { product: string; topic: string } | null {
    if (!this.isBdsmbeds()) return null;
    const product = this.route.snapshot.queryParamMap.get('product')?.trim() ?? '';
    const topicKey = this.route.snapshot.queryParamMap.get('topic')?.trim() ?? '';
    if (!product) return null;
    const topic = this.bdsmbedsTopics.find((item) => item.key === topicKey)?.title ?? 'Product question';
    return { product: product.replace(/-/g, ' '), topic };
  }

  protected selectSupportTopic(topic: string): void {
    const current = this.supportContext();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { topic, product: current?.product?.replace(/\s+/g, '-') || null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  protected async beginTicket(method: SupportMethod): Promise<void> {
    if (method.id <= 0) {
      this.ticketError.set('Bu destek türü henüz yapılandırılmamış. Lütfen destek taleplerim sayfasını açın.');
      return;
    }
    this.ticketBusy.set(true);
    this.ticketError.set('');
    try {
      const detail = await this.support.method(method.id);
      this.selectedMethod.set(method);
      this.ticketFields.set(detail.formFields);
      Object.keys(this.ticketValues).forEach((key) => delete this.ticketValues[key]);
      this.prefillBdsmbedsContext(detail.formFields);
    } catch (error) {
      this.ticketError.set(error instanceof Error ? error.message : 'Destek formu açılamadı.');
    } finally {
      this.ticketBusy.set(false);
    }
  }

  private prefillBdsmbedsContext(fields: SupportFormField[]): void {
    const context = this.supportContext();
    if (!context) return;
    const subject = fields.find((field) => /subject|title|topic|konu/i.test(`${field.formKey} ${field.title}`));
    const message = fields.find((field) => field.type === 'textarea' || field.type === 'longtext' || /message|description|detail|mesaj|açıklama/i.test(`${field.formKey} ${field.title}`));
    if (subject) this.ticketValues[subject.formKey] = `${context.topic}: ${context.product}`;
    if (message) this.ticketValues[message.formKey] = `Product: ${context.product}\nTopic: ${context.topic}\n\nPlease add the details you would like us to confirm.`;
  }

  protected cancelTicket(): void {
    this.selectedMethod.set(null);
    this.ticketFields.set([]);
    this.ticketError.set('');
    Object.keys(this.ticketValues).forEach((key) => delete this.ticketValues[key]);
  }

  protected async submitTicket(): Promise<void> {
    const method = this.selectedMethod();
    if (!method || method.id <= 0 || this.ticketBusy()) return;
    const validationError = this.ticketValidationError();
    if (validationError) {
      this.ticketError.set(validationError);
      return;
    }
    this.ticketBusy.set(true);
    this.ticketError.set('');
    try {
      await this.support.createTicket(method.id, this.ticketValues, this.currentCategory()?.id ?? 0);
    } catch (error) {
      this.ticketError.set(error instanceof Error ? error.message : 'Destek talebi oluşturulamadı.');
      this.ticketBusy.set(false);
      return;
    }
    // Ticket creation is already committed. A lazy-route/navigation failure
    // must never be reported as if the support request itself had failed.
    try {
      const navigated = await this.router.navigateByUrl(this.i18n.routePath('supportTickets'));
      if (!navigated && typeof window !== 'undefined') window.location.assign(this.i18n.routePath('supportTickets'));
    } catch {
      if (typeof window !== 'undefined') window.location.assign(this.i18n.routePath('supportTickets'));
    } finally {
      this.ticketBusy.set(false);
    }
  }

  protected fieldLength(field: SupportFormField): number {
    const value = this.ticketValues[field.formKey];
    return Array.isArray(value) ? value.length : String(value ?? '').length;
  }

  private ticketValidationError(): string {
    for (const field of this.ticketFields()) {
      const value = String(this.ticketValues[field.formKey] ?? '').trim();
      const length = value.length;
      if (field.required && !length) return `${field.title}: ${this.copy().requiredField}.`;
      if (length && field.minlength && length < field.minlength) return `${field.title}: ${this.copy().minimum} ${field.minlength} ${this.copy().characters} (${this.copy().current}: ${length}).`;
      if (length && field.maxlength && length > field.maxlength) return `${field.title}: ${this.copy().maximum} ${field.maxlength} ${this.copy().characters} (${this.copy().current}: ${length}).`;
      if (length && field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `${field.title}: ${this.copy().invalidEmail}.`;
    }
    return '';
  }

  private async load(slug: string): Promise<void> {
    const seeded = this.loadSeeded(slug);
    if (seeded) {
      return;
    }

    this.seo.setPage({
      title: slug ? this.i18n.t('supportDetail') : this.i18n.t('support'),
      description: this.supportDescription(),
      path: slug ? `${this.i18n.routePath('support').replace(/\/$/, '')}/${slug}` : this.i18n.routePath('support'),
      breadcrumbs: this.breadcrumbs(slug ? this.i18n.t('supportDetail') : this.i18n.t('support'), slug)
    });

    if (!slug) {
      const payload = await this.support.center();
      this.currentCategory.set(null);
      this.visibleCategories.set(this.tenantCategories(payload.categories.filter((category) => this.isRootCategory(category))));
      this.faqs.set([]);
      this.helpText.set(null);
      this.methods.set([]);
      return;
    }

    const payload = await this.support.category(slug);
    this.currentCategory.set(payload.category);
    this.visibleCategories.set(this.tenantCategories(payload.subCategories));
    this.faqs.set(payload.faqs);
    this.helpText.set(payload.helpText);
    this.methods.set(payload.methods.length || payload.subCategories.length ? payload.methods : [this.defaultTicketMethod()]);
    if (payload.category) {
      this.seo.setPage({
        title: `${payload.category.name} ${this.i18n.t('supportCategorySuffix')}`,
        description: `${payload.category.name} ${this.i18n.t('supportCategoryDescription')}`,
        path: this.categoryLink(payload.category),
        breadcrumbs: this.breadcrumbs(payload.category.name, slug)
      });
    }
  }

  private loadSeeded(slug: string): boolean {
    const seeded = this.extractPayload(this.pageData.get<unknown>(slug ? 'supportCategory' : 'supportCenter'), slug ? 'supportCategory' : 'supportCenter');
    if (!seeded) {
      return false;
    }
    const category = this.normalizeCategory(seeded['category'] ?? seeded['selectedCategory']);
    this.currentCategory.set(category);
    const categories = this.normalizeCategories(seeded['categories']);
    this.visibleCategories.set(this.tenantCategories(slug ? this.normalizeCategories(seeded['subCategories']) : categories.filter((item) => this.isRootCategory(item))));
    this.faqs.set(this.normalizeFaqs(seeded['faqs']));
    this.helpText.set(typeof seeded['helpText'] === 'string' ? seeded['helpText'] : null);
    const seededMethods = this.normalizeMethods(seeded['methods']);
    const seededSubCategories = this.normalizeCategories(seeded['subCategories']);
    this.methods.set(seededMethods.length || seededSubCategories.length ? seededMethods : [this.defaultTicketMethod()]);
    const title = category?.name || this.i18n.t('support');
    this.seo.setPage({
      title: category ? `${category.name} ${this.i18n.t('supportCategorySuffix')}` : this.i18n.t('support'),
      description: category ? `${category.name} ${this.i18n.t('supportCategoryDescription')}` : this.supportDescription(),
      path: category ? this.categoryLink(category) : this.i18n.routePath('support'),
      breadcrumbs: this.breadcrumbs(title, slug)
    });
    return true;
  }

  private breadcrumbs(label: string, slug: string): Array<{ name: string; path: string }> {
    const items = [
      { name: this.i18n.t('home'), path: this.i18n.routePath('home') },
      { name: this.i18n.t('support'), path: this.i18n.routePath('support') }
    ];
    return slug ? [...items, { name: label, path: `${this.i18n.routePath('support').replace(/\/$/, '')}/${slug}` }] : items;
  }

  private extractPayload(response: unknown, key: string): Record<string, unknown> | null {
    const root = response as Record<string, unknown> | null;
    const data = root?.['data'] as Record<string, unknown> | undefined;
    const apiData = root?.['apiData'] as Record<string, unknown> | undefined;
    const direct = data?.[key] ?? apiData?.[key] ?? data;
    return direct && typeof direct === 'object' ? direct as Record<string, unknown> : null;
  }

  private normalizeCategories(value: unknown): SupportCategory[] {
    return Array.isArray(value) ? value.map((item) => this.normalizeCategory(item)).filter((item): item is SupportCategory => !!item) : [];
  }

  private normalizeCategory(value: unknown): SupportCategory | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const item = value as Record<string, unknown>;
    return {
      id: Number(item['id'] ?? 0),
      name: String(item['name'] ?? item['title'] ?? 'Destek'),
      title: String(item['title'] ?? item['name'] ?? 'Destek'),
      slug: String(item['slug'] ?? item['id'] ?? ''),
      parentID: item['parentID'] ? Number(item['parentID']) : null,
      imageUrl: typeof item['imageUrl'] === 'string' ? item['imageUrl'] : null,
      hasChildren: Boolean(item['hasChildren'])
    };
  }

  private isRootCategory(category: SupportCategory): boolean {
    return category.parentID === null || category.parentID === 0;
  }

  private tenantCategories(categories: SupportCategory[]): SupportCategory[] {
    if (this.site.config().brandKey !== 'bdsmbeds') return categories;
    const fortuneOnly = /fal|fortune|reading|kahve|tarot|canl[ıi]\s*yay|kredi|credit/i;
    return categories.filter((category) =>
      !fortuneOnly.test(`${category.name} ${category.title} ${category.slug}`),
    );
  }

  private supportDescription(): string {
    return this.site.config().brandKey === 'bdsmbeds'
      ? 'Get help with BDSMBeds products, orders, delivery, returns and account questions.'
      : this.i18n.t('supportSeoDescription');
  }

  protected defaultTicketMethod(): SupportMethod {
    return {
      id: 0,
      title: this.copy().createTicket,
      description: this.copy().generalTicketLead,
      type: 'ticket'
    };
  }

  private normalizeFaqs(value: unknown): SupportFaq[] {
    return Array.isArray(value) ? value.map((item) => {
      const row = item as Record<string, unknown>;
      return { id: Number(row['id'] ?? 0), title: String(row['title'] ?? ''), content: String(row['content'] ?? row['description'] ?? '') };
    }) : [];
  }

  private normalizeMethods(value: unknown): SupportMethod[] {
    return Array.isArray(value) ? value.map((item) => {
      const row = item as Record<string, unknown>;
      return { id: Number(row['id'] ?? 0), title: String(row['title'] ?? ''), description: String(row['description'] ?? ''), type: String(row['type'] ?? 'ticket') };
    }) : [];
  }
}
